import { PAYMENT_CONFIG } from '../constants/appConstants';

/**
 * Format price with currency symbol
 */
export const formatPrice = (price: number): string => {
  return `${PAYMENT_CONFIG.CURRENCY_SYMBOL}${price.toFixed(2)}`;
};

/**
 * Calculate tax amount
 */
export const calculateTax = (subtotal: number): number => {
  return subtotal * PAYMENT_CONFIG.TAX_RATE;
};

/**
 * Calculate total with tax
 */
export const calculateTotal = (subtotal: number): number => {
  return subtotal + calculateTax(subtotal);
};

/**
 * Generate receipt number
 */
export const generateReceiptNumber = (): string => {
  const timestamp = Date.now().toString().slice(-8);
  return `RCP-${timestamp}`;
};

/**
 * Format date for display
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString();
};

/**
 * Format time for display
 */
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString();
};

/**
 * Format date and time for display
 */
export const formatDateTime = (date: Date): string => {
  return `${formatDate(date)} ${formatTime(date)}`;
};

/**
 * Format barcode for display
 */
export const formatBarcode = (barcode: string): string => {
  // Add spaces every 4 characters for better readability
  return barcode.replace(/(.{4})/g, '$1 ').trim();
};

/**
 * Validate barcode format
 */
export const isValidBarcode = (barcode: string): boolean => {
  const pattern = /^[0-9A-Za-z]{8,20}$/;
  return pattern.test(barcode);
};

/**
 * Format student ID
 */
export const formatStudentId = (id: string): string => {
  return id.toUpperCase();
};

/**
 * Format card number for display (masked)
 */
export const formatCardNumber = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');
  const masked = cleaned.slice(0, 4) + ' **** **** ' + cleaned.slice(-4);
  return masked;
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

/**
 * Format postal code
 */
export const formatPostalCode = (postalCode: string): string => {
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  if (cleaned.length === 6) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  }
  return postalCode;
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
};

/**
 * Capitalize first letter of each word
 */
export const capitalizeWords = (text: string): string => {
  return text.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};
